import React, { useMemo, useState } from 'react';
import _ from 'lodash';
import { Input } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import randomColor from 'randomcolor';
import * as Utility from '../../HelperMethods/UtilityFunctions';
import { getDisplayLabelIfUserIsAvailable } from '../../HelperMethods/UserUtilities';
import { useGetUsersOfUserSideBar } from '../common/hooks';
import styles from './LeftPanelGrid.module.css';
import { selectStop } from '../map/redux/actions';
import { userIcon, equipmentResourceIcon } from '../../common/constants';
import { doesTaskTypeBelongToSelectedGroup } from '../../HelperMethods/TaskUtilities';

const UserPane = ({ onUserSelect, selectedUserIds, selectedTaskTypeGroups, taskTypes }) => {
  const dispatch = useDispatch();
  const users = useGetUsersOfUserSideBar();
  const [searchText, setSearchText] = useState('');
  const iframepresentState = useSelector((state) => state.taskViewPageReducer.iframepresentState);

  const usersToDisplay = useMemo(() => {
    const filteredTaskType = taskTypes.filter((tt) => {
      if (!selectedTaskTypeGroups?.length) {
        return true;
      }
      return doesTaskTypeBelongToSelectedGroup(tt, selectedTaskTypeGroups);
    });
    let teamIds = filteredTaskType.map((tt) => tt.teamId);
    teamIds = _.uniq(teamIds);
    // const userTeamIds = u.teams.filter((t) => !t.isDeleted).map((t) => t.teamId);
    // return _.intersection(teamIds, userTeamIds)?.length;
    const filterUsers = users
      .filter((u) => {
        if (!selectedTaskTypeGroups?.length || !teamIds?.length) {
          return true;
        }
        const userTeamIds = u.teams.filter((t) => !t.isDeleted).map((t) => t.teamId);
        return _.intersection(teamIds, userTeamIds)?.length;
      })
      .map((user) => {
        const userAvailabilityByHitch = Utility.isUserAvailableForDate(new Date(), user);
        return {
          ...user,
          displayLabel: userAvailabilityByHitch ? getDisplayLabelIfUserIsAvailable(user) : null,
          background: userAvailabilityByHitch ? '#00cfbb' : '#eb5757',
          userStatus: userAvailabilityByHitch ? 'Available' : 'Not Available',
        };
      })
      .sort((u1, u2) => {
        const statusCompared = u1.userStatus.localeCompare(u2.userStatus);
        if (statusCompared === 0) return `${u1.firstName}${u1.lastName}`.localeCompare(`${u2.firstName}${u2.lastName}`);
        return statusCompared;
      });
    return filterUsers;
  }, [selectedTaskTypeGroups, taskTypes, users]);

  const filteredUsers = useMemo(() => {
    return usersToDisplay.filter((user) => {
      if (!searchText) return true;
      const name = user.firstName + user.lastName;
      return name.toLowerCase().includes(searchText);
    });
  }, [usersToDisplay, searchText]);

  return (
    <div
      className={!iframepresentState ? styles.leftPanelSubContainer : styles.leftPanelSubContainerForIfram}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Input
        icon="search"
        placeholder="Search"
        value={searchText}
        className={styles.searchInput}
        onChange={(event, { value }) => {
          setSearchText(value);
        }}
      />
      <div className={styles.listContainer}>
        {filteredUsers.map((user) => {
          const { isEquipmentResource = false } = user;
          return (
            <div
              key={user.id}
              onClick={() => {
                onUserSelect(user);
                dispatch(selectStop(null));
              }}
              style={
                selectedUserIds.includes(user.id)
                  ? {
                      background: randomColor({
                        seed: user.id,
                        luminosity: 'bright',
                        format: 'rgba',
                        alpha: 0.2,
                      }),
                      borderLeft: `4px solid ${randomColor({
                        seed: user.id,
                        luminosity: 'bright',
                        format: 'rgba',
                        alpha: 1,
                      })}`,
                    }
                  : {}
              }
              className={styles.box}
            >
              <div className={styles.boxHeader}>
                {isEquipmentResource ? (
                  <img
                    src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(equipmentResourceIcon)}`}
                    alt="Discard"
                    style={{ marginRight: '3px' }}
                  />
                ) : (
                  <img
                    style={{ marginRight: '3px' }}
                    src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(userIcon)}`}
                    alt="Discard"
                  />
                )}
                <span>{`${user.firstName} ${user.lastName}`}</span>
              </div>
              <div className={styles.boxSubHeader}>
                <div className={styles.userStatusContainer}>
                  <div
                    style={{
                      background: user.background,
                    }}
                    className={styles.userStatusSymbol}
                  />
                  <div className={styles.userStatusText}>{user.userStatus}</div>
                </div>
                {user.displayLabel && <div>{user.displayLabel}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

UserPane.propTypes = {
  onUserSelect: PropTypes.func.isRequired,
  selectedUserIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedTaskTypeGroups: PropTypes.arrayOf(PropTypes.object),
  taskTypes: PropTypes.arrayOf(PropTypes.object),
};

export default UserPane;
